import React, { useState } from 'react';
import { Card, Table, Button, Form, Badge, Pagination } from 'react-bootstrap';
import moment from 'moment';
import { FaEdit, FaTrash, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

const placeholderStyle = `
  .white-placeholder::placeholder {
    color: white !important;
    opacity: 0.7;
  }
`;

const TransactionList = ({ transactions, onEdit, onDelete, bankAccounts }) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: 'datetime',
    direction: 'desc'
  });

  const ITEMS_PER_PAGE = 20;

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FaSort className="ms-1" />;
    }
    return sortConfig.direction === 'asc' ? 
      <FaSortUp className="ms-1" /> : 
      <FaSortDown className="ms-1" />;
  };

  const getBankAccountName = (accountId) => {
    const account = bankAccounts.find(acc => acc.id === accountId);
    return account ? account.bank : 'Unknown Account';
  };

  const sortedTransactions = [...transactions].sort((a, b) => {
    if (!sortConfig.key) return 0;

    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    // Handle special cases
    if (sortConfig.key === 'datetime') {
      aValue = moment(a.datetime).valueOf();
      bValue = moment(b.datetime).valueOf();
    } else if (sortConfig.key === 'amount') {
      aValue = Number(a.amount);
      bValue = Number(b.amount);
    }

    if (aValue < bValue) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const filteredTransactions = sortedTransactions
    .filter(transaction => {
      if (filter === 'all') return true;
      if (filter === 'incoming') return transaction.incoming;
      if (filter === 'outgoing') return !transaction.incoming;
      return true;
    })
    .filter(transaction => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (transaction.description || '').toLowerCase().includes(searchLower) ||
        (transaction.category || '').toLowerCase().includes(searchLower) ||
        (transaction.counterparty || '').toLowerCase().includes(searchLower) ||
        (transaction.account_name || '').toLowerCase().includes(searchLower)
      );
    });

  // Calculate pagination
  const totalPages = Math.ceil(filteredTransactions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTransactions = filteredTransactions.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <Card className="bg-dark text-light">
      <style>{placeholderStyle}</style>
      <Card.Header className="d-flex justify-content-between align-items-center bg-dark text-light">
        <div className="d-flex gap-3 align-items-center">
          <Form.Select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-secondary text-light"
            style={{ width: 'auto' }}
          >
            <option value="all">All Transactions</option>
            <option value="incoming">Incoming</option>
            <option value="outgoing">Outgoing</option>
          </Form.Select>

          <Form.Control
            type="text"
            placeholder="Search description, who, category..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-secondary text-light white-placeholder"
            style={{ width: '300px' }}
          />
        </div>
      </Card.Header>
      <Card.Body>
        <Table striped bordered hover variant="dark" className="align-middle">
          <thead>
            <tr>
              <th 
                className="text-center"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('datetime')}
              >
                Date/Time {getSortIcon('datetime')}
              </th>
              <th 
                className="text-center"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('description')}
              >
                Description {getSortIcon('description')}
              </th>
              <th className="text-center">Who</th>
              <th 
                className="text-center"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('category')}
              >
                Category {getSortIcon('category')}
              </th>
              <th 
                className="text-center"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('amount')}
              >
                Amount {getSortIcon('amount')}
              </th>
              <th 
                className="text-center"
                style={{ cursor: 'pointer' }}
                onClick={() => handleSort('bank_account')}
              >
                Bank Account {getSortIcon('bank_account')}
              </th>
              <th className="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedTransactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="text-center">{moment(transaction.datetime).format('DD-MM-YYYY HH:mm')}</td>
                <td className="text-center">{transaction.description || '—'}</td>
                <td className="text-center">{transaction.counterparty || '—'}</td>
                <td className="text-center">
                  <Badge bg="info" style={{ fontSize: '0.85rem', padding: '0.35rem 0.6rem' }}>
                    {transaction.category?.replace(/_/g, ' ') || '—'}
                  </Badge>
                </td>
                <td className={`text-center ${transaction.incoming ? "text-success" : "text-danger"}`}>
                  {transaction.incoming ? '+' : '−'}€{Number(transaction.amount).toFixed(2)}
                </td>
                <td className="text-center">{transaction.account_name || getBankAccountName(transaction.bank_account)}</td>
                <td className="text-center">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-2 hover-white"
                    onClick={() => onEdit(transaction)}
                    style={{ 
                      borderColor: '#42bdff',
                      color: '#42bdff'
                    }}
                  >
                    <FaEdit className="me-1" /> Edit
                  </Button>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onDelete(transaction.id)}
                  >
                    <FaTrash className="me-1" /> Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination className="custom-pagination">
              <Pagination.First 
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === currentPage}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last 
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default TransactionList; 